import { toastr } from 'react-redux-toastr';
import { I18n } from 'src/translations/i18n';
import { getAccessGroups, getAccessKeysByUser, getUserGroupMappingsByUser, createAccessUser, createAccessKey, assignIdKey, createUserGroupMapping, deleteAccessUser, deleteAccessKey, deleteUserGroupMapping } from 'src/api/mu4Api';
// Action types
export const LOAD_MU4_ACCESS_GROUPS_REQUEST = 'LOAD_MU4_ACCESS_GROUPS_REQUEST';
export const LOAD_MU4_ACCESS_GROUPS_SUCCESS = 'LOAD_MU4_ACCESS_GROUPS_SUCCESS';
export const LOAD_MU4_ACCESS_GROUPS_FAIL = 'LOAD_MU4_ACCESS_GROUPS_FAIL';
export const CREATE_MU4_ACCESS_USER_REQUEST = 'CREATE_MU4_ACCESS_USER_REQUEST';
export const CREATE_MU4_ACCESS_USER_SUCCESS = 'CREATE_MU4_ACCESS_USER_SUCCESS';
export const CREATE_MU4_ACCESS_USER_FAIL = 'CREATE_MU4_ACCESS_USER_FAIL';
export const DELETE_MU4_ACCESS_USER_REQUEST = 'DELETE_MU4_ACCESS_USER_REQUEST';
export const DELETE_MU4_ACCESS_USER_SUCCESS = 'DELETE_MU4_ACCESS_USER_SUCCESS';
export const DELETE_MU4_ACCESS_USER_FAIL = 'DELETE_MU4_ACCESS_USER_FAIL';
export function getMU4ErrorMessage(error) {
    const messages = error.response?.data?.messages;
    if (messages?.length) {
        const currentLang = I18n.language?.iso6391;
        const localized = messages.find((m) => m.languageCode === currentLang);
        return localized?.text || messages[0]?.text || error.message;
    }
    return error.message || I18n.t('UnknownErrorTryLater');
}
function mu4ErrorToastr(error) {
    const message = getMU4ErrorMessage(error);
    toastr.error(I18n.t('Error'), message);
}
// --- Load Access Groups ---
// The employee card asks again while the list is empty, so a failed first load does not stay until a page reload.
// A load that is already running is not started a second time.
export const loadMU4AccessGroups = () => (dispatch, getState) => {
    if (getState().mu4?.accessGroupsFetching)
        return Promise.resolve();
    dispatch({ type: LOAD_MU4_ACCESS_GROUPS_REQUEST });
    return getAccessGroups()
        .then(({ data }) => {
        dispatch({ type: LOAD_MU4_ACCESS_GROUPS_SUCCESS, accessGroups: data });
    })
        .catch((error) => {
        dispatch({ type: LOAD_MU4_ACCESS_GROUPS_FAIL, error });
        mu4ErrorToastr(error);
    });
};
// 409 from IdKey/assign: the pool card was issued to someone else in the meantime
export function isMU4ConflictError(error) {
    return error?.response?.status === 409;
}
// A pool card is issued by its code; a code that is not in the pool (404) is created by hand, as before the pool
async function issueMU4Key(keyType, value, userId) {
    try {
        const { data } = await assignIdKey({ type: keyType, value, userId });
        return data.accessKey.id;
    }
    catch (error) {
        if (error.response?.status !== 404)
            throw error;
    }
    const { data } = await createAccessKey({ type: keyType, value, userId });
    return data.id;
}
export const createMU4AccessUserFull = (params) => async (dispatch) => {
    dispatch({ type: CREATE_MU4_ACCESS_USER_REQUEST });
    try {
        let mu4UserId;
        const isNewUser = !params.existingMu4UserId;
        if (isNewUser) {
            const userResponse = await createAccessUser({
                firstName: params.firstName,
                lastName: params.lastName
            });
            mu4UserId = userResponse.data.id;
        }
        else {
            mu4UserId = params.existingMu4UserId;
        }
        let mu4KeyId;
        try {
            mu4KeyId = await issueMU4Key(params.keyType, params.cardHexValue, mu4UserId);
        }
        catch (keyError) {
            if (isNewUser) {
                await deleteAccessUser(mu4UserId).catch(() => { });
            }
            throw keyError;
        }
        const result = {
            mu4UserId,
            mu4KeyId,
            cardHexValue: params.cardHexValue,
            isNewUser
        };
        dispatch({ type: CREATE_MU4_ACCESS_USER_SUCCESS, payload: result });
        toastr.success(I18n.t('Success'), '');
        return result;
    }
    catch (error) {
        dispatch({ type: CREATE_MU4_ACCESS_USER_FAIL, error });
        if (!(params.fromPool && isMU4ConflictError(error))) {
            mu4ErrorToastr(error);
        }
        throw error;
    }
};
export const loadMU4CardsForUser = (mu4UserId) => async () => {
    try {
        const keys = (await getAccessKeysByUser(mu4UserId)).filter((k) => k.data.userId === mu4UserId);
        return keys.map((keyRecord) => ({
            mu4UserId,
            mu4KeyId: keyRecord.id,
            cardHexValue: keyRecord.data.value,
            keyType: keyRecord.data.type,
            seqNr: keyRecord.data.seqNr ?? null
        }));
    }
    catch (error) {
        mu4ErrorToastr(error);
        throw error;
    }
};
// --- Delete single MU4 card (AccessKey); cascade user cleanup if last one ---
export const deleteMU4CardFull = (mu4UserId, mu4KeyId) => async (dispatch) => {
    dispatch({ type: DELETE_MU4_ACCESS_USER_REQUEST });
    try {
        await deleteAccessKey(mu4KeyId);
        const remainingKeys = (await getAccessKeysByUser(mu4UserId)).filter((k) => k.data.userId === mu4UserId);
        const userFullyDeleted = remainingKeys.length === 0;
        if (userFullyDeleted) {
            const userMappings = (await getUserGroupMappingsByUser(mu4UserId)).filter((m) => m.data.userId === mu4UserId);
            await Promise.all(userMappings.map((m) => deleteUserGroupMapping(m.id)));
            await deleteAccessUser(mu4UserId);
        }
        dispatch({ type: DELETE_MU4_ACCESS_USER_SUCCESS, payload: { mu4UserId, mu4KeyId } });
        toastr.success(I18n.t('Success'), '');
        return { userFullyDeleted };
    }
    catch (error) {
        dispatch({ type: DELETE_MU4_ACCESS_USER_FAIL, error });
        mu4ErrorToastr(error);
        throw error;
    }
};
const toMU4UserGroupMapping = (m) => ({
    mappingId: m.id,
    accessGroupId: m.data.accessGroupId,
    validFromUtc: m.data.validFromUtc ?? null,
    validToUtc: m.data.validToUtc ?? null
});
export const loadMU4UserGroups = (mu4UserId) => async () => {
    try {
        const userMappings = (await getUserGroupMappingsByUser(mu4UserId)).filter((m) => m.data.userId === mu4UserId);
        return userMappings
            .filter((m) => typeof m.data.accessGroupId === 'number')
            .map(toMU4UserGroupMapping);
    }
    catch (error) {
        // Not an empty list: the card must not offer profiles the employee may already have
        mu4ErrorToastr(error);
        throw error;
    }
};
// A profile is written as soon as it is added or removed, the same way as the keys. The added one is returned, so
// the card can list it without waiting for a reload.
export const addMU4UserGroup = (mu4UserId, draft) => async () => {
    try {
        const { data } = await createUserGroupMapping({ userId: mu4UserId, ...draft });
        toastr.success(I18n.t('Success'), '');
        return toMU4UserGroupMapping(data);
    }
    catch (error) {
        mu4ErrorToastr(error);
        throw error;
    }
};
export const removeMU4UserGroup = (mappingId) => async () => {
    try {
        await deleteUserGroupMapping(mappingId);
        toastr.success(I18n.t('Success'), '');
    }
    catch (error) {
        mu4ErrorToastr(error);
        throw error;
    }
};
